import React, { useState, useEffect, useRef } from "react";
import { Building2, ChevronDown, Check } from "lucide-react";
import { useBranch } from "../../contexts/BranchContext";
import "../Branches/branches.scss";

const BranchSelector = () => {
  const { branches, selectedBranchId, selectBranch, isMultiBranch, canSelectAll } = useBranch();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (!isMultiBranch || branches.length === 0) return null;

  const activeBranches = branches.filter(b => b.isActive);
  const selectedBranch = branches.find(b => b._id === selectedBranchId);

  // Exactly one reachable branch — there is nothing to choose, but the user
  // still needs to see the scope they are working in. Rendering nothing here
  // (the old behaviour) left a branch-restricted admin with no indication that
  // the numbers on screen were their branch's rather than the institute's.
  const hasChoice = activeBranches.length > 1 || canSelectAll;
  if (!hasChoice) {
    const only = selectedBranch || activeBranches[0];
    if (!only) return null;
    return (
      <div className="branch-selector">
        <span className="branch-selector__trigger branch-selector__trigger--static" title={`Branch: ${only.name}`}>
          <Building2 size={14} />
          <span className="branch-selector__label">{only.name}</span>
        </span>
      </div>
    );
  }

  const displayLabel = selectedBranch ? selectedBranch.name : "All Branches";

  return (
    <div className="branch-selector" ref={ref}>
      <button
        type="button"
        className="branch-selector__trigger"
        onClick={() => setOpen(!open)}
      >
        <Building2 size={14} />
        <span className="branch-selector__label">{displayLabel}</span>
        <ChevronDown size={14} />
      </button>

      {open && (
        <div className="branch-selector__dropdown">
          {/* Offered only when the caller actually holds an institute-wide
              scope. For a branch-restricted admin the API rejects it, so
              showing it would just be a button that returns 403. */}
          {canSelectAll && (
            <button
              type="button"
              className={`branch-selector__item ${selectedBranchId === 'all' ? 'branch-selector__item--active' : ''}`}
              onClick={() => { selectBranch('all'); setOpen(false); }}
            >
              All Branches
              {selectedBranchId === 'all' && <Check size={14} className="branch-selector__check" />}
            </button>
          )}
          {activeBranches.map((b) => (
            <button
              key={b._id}
              type="button"
              className={`branch-selector__item ${selectedBranchId === b._id ? 'branch-selector__item--active' : ''}`}
              onClick={() => { selectBranch(b._id); setOpen(false); }}
            >
              {b.name}
              {selectedBranchId === b._id && <Check size={14} className="branch-selector__check" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BranchSelector;
